import { Middleware } from '@reduxjs/toolkit';

import {
  choosePaymentMethod,
  confirmPaymentFailure,
  confirmPaymentSuccess,
  fetchPaymentCurrentState,
  fetchPaymentCurrentStateSuccess,
  fetchPaymentInfoSuccess,
  processPaymentFailure,
  processPaymentSuccess,
  resetConfirmationFormData,
  resetPaymentMethod,
  resetStore,
  selectPaymentInputs,
  selectPaymentMethod,
  selectProcessingResult,
  setActiveScreen,
  setAmount,
} from '@cashier/cashier-data-access';
import {
  CashierMessages,
  DynamicScreens,
  PaymentActionResult,
  PaymentStateInfo,
  PaymentStates,
  RedirectContainers,
} from '@cashier/cashier-model';
import {
  eventBus,
  EventFactory,
  PaymentStateChecker,
  ProviderWindowService,
} from '@cashier/cashier-shared';

import {
  FinteqHubService,
  getCookie,
  isFinteqHubCardDeposit,
  QaToolKitOptions,
} from '../helpers';

export const commonMiddleware: Middleware =
  (store) => (next) => async (action) => {
    const result = next(action);
    const {
      cashierConfiguration: { notifyCustomerAboutPaymentResult, activeType },
    } = store.getState();

    if (action.type === fetchPaymentInfoSuccess.toString()) {
      const { payload }: { payload: PaymentStateInfo } = action;

      if (payload.state === PaymentStates.PROCESSING) {
        store.dispatch(fetchPaymentCurrentState(payload.transactionId));
      }
    }

    if (action.type === fetchPaymentCurrentStateSuccess.toString()) {
      const { payload }: { payload: PaymentStateInfo } = action;
      const {
        customerInformation: { activeScreen },
      } = store.getState();

      if (activeScreen !== DynamicScreens.PaymentForm) {
        if (payload.state === PaymentStates.PROCESSING) {
          store.dispatch(fetchPaymentCurrentState(payload.transactionId));
        }

        if (
          PaymentStateChecker.isFinalized(activeType, payload.state) &&
          !notifyCustomerAboutPaymentResult
        ) {
          eventBus.emit(
            EventFactory.paymentProcessed(
              [
                PaymentStates.FORCE_SUCCESSFUL,
                PaymentStates.SUCCESSFUL,
              ].includes(payload.state)
                ? 200
                : 500,
              {},
            ),
          );
        }
      }
    }

    if (action.type === choosePaymentMethod.toString()) {
      const {
        paymentProcessing: { result },
      } = store.getState();

      if (result?.errors) {
        store.dispatch(resetStore());
      }

      document.documentElement.scrollTop = 0;
    }

    /* resetAmount after any changes in PaymentMethod */
    if (
      [choosePaymentMethod.toString(), resetPaymentMethod.toString()].includes(
        action.type,
      )
    ) {
      store.dispatch(setActiveScreen(DynamicScreens.PaymentForm));
      store.dispatch(setAmount(null));
    }

    //proxy for processPaymentSuccess
    if (action.type === confirmPaymentSuccess.toString()) {
      const { payload }: { payload: PaymentActionResult } = action;
      const paymentMethod = selectPaymentMethod(store.getState());

      // TODO delete after testing 2914
      const isHostedFieldEnabled =
        getCookie(QaToolKitOptions.enableHostedField) === 'true';

      if (isFinteqHubCardDeposit(paymentMethod.preprocessor)) {
        if (isHostedFieldEnabled) {
          const processingResult = selectProcessingResult(store.getState());

          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.SendRequestForFinteqHubMethods,
              processingResult: processingResult,
            }),
          );

          return;
        } else {
          // TODO delete after testing 2914
          try {
            const paymentInputs = selectPaymentInputs(store.getState());
            const processingResult = selectProcessingResult(store.getState());

            const response = await FinteqHubService.processPayment(
              processingResult.paymentAttributes,
              paymentInputs,
              paymentMethod.preprocessor,
            );

            const isRedirect =
              response.type === 'redirect' &&
              response.redirectUrl &&
              RedirectContainers.NEW_TAB === paymentMethod.redirectContainer;

            if (isRedirect) {
              ProviderWindowService.redirect(
                response.redirectUrl,
                paymentMethod.redirectContainer === RedirectContainers.NEW_TAB,
              );

              return;
            }

            store.dispatch(processPaymentSuccess(payload));
          } catch (error) {
            store.dispatch(
              processPaymentFailure({
                message: error,
                attributeErrorBag: null,
                errorDetails: { description: error },
                transactionId: error.transactionId,
              }),
            );
          }

          return;
        }
      }

      store.dispatch(resetConfirmationFormData());
      store.dispatch(processPaymentSuccess(payload));
    }

    if (action.type === confirmPaymentFailure.toString()) {
      store.dispatch(processPaymentFailure(action.payload));
    }

    return result;
  };
